import {
  type AnonCategory,
  type Entity,
  hintedNames,
  type KeyRow,
  LIST_CATEGORIES,
  type ListCategory,
  type NameCandidate,
  parseKey,
  parseTermList,
  suggestCandidates,
} from "@/lib/tool-kit/qualitative-text/anonymize-names";
import {
  detectPatterns,
  type PatternCategory,
  type PatternHit,
  yearOfDate,
} from "@/lib/tool-kit/qualitative-text/anonymize-patterns";

export type PseudonymStyle = "tags" | "labels" | "category";
export type DateMode = "tag" | "year";

export interface AnonymizeOptions {
  /** One list per category, one term per line. */
  lists: Record<ListCategory, string>;
  detect: Record<PatternCategory, boolean>;
  speakers: boolean;
  titles: boolean;
  intros: boolean;
  /** Also match the parts of a multi-word name (the first name, the surname). */
  nameParts: boolean;
  matchCase: boolean;
  style: PseudonymStyle;
  dateMode: DateMode;
  /** The word for each category, as it appears in a pseudonym ("Person" → "Person 1"). */
  labels: Record<AnonCategory, string>;
  /** A key file from an earlier run, so the same people get the same pseudonyms. */
  existingKey: string;
}

export interface Replacement {
  start: number;
  end: number;
  category: AnonCategory;
  original: string;
  replacement: string;
}

export interface AnonymizeResult {
  output: string;
  replacements: Replacement[];
  key: KeyRow[];
  counts: Record<AnonCategory, number>;
  /** Parts of names that belong to more than one person and so were left alone. */
  ambiguous: string[];
  candidates: NameCandidate[];
}

interface RawMatch {
  start: number;
  end: number;
  category: AnonCategory;
  /** Same identity, same pseudonym. */
  identity: string;
  /** Lower number wins a tie for the same span. */
  priority: number;
}

interface VariantInfo {
  entity: number;
  /** Matched only when the text spells it with a capital: a part, or a hinted name. */
  capsOnly: boolean;
}

interface Matcher {
  regex: RegExp | null;
  lookup: Map<string, VariantInfo>;
  ambiguous: string[];
}

const RE_ESCAPE = /[.*+?^${}()|[\]\\]/g;
const RE_SPACES = /\s+/g;
const RE_UPPER_START = /^\p{Lu}/u;
const RE_NAME_WORDS = /[ \t]+/;
const RE_TRAILING_DOT = /\.$/;
const MIN_PART = 3;
const PARTICLES = new Set(
  "van von der den del della di da de la le du des bin ibn al el of the and jr sr ii iii".split(
    " "
  )
);

function norm(value: string, matchCase: boolean): string {
  const collapsed = value.replace(RE_SPACES, " ").trim();
  return matchCase ? collapsed : collapsed.toLowerCase();
}

function nameParts(variants: readonly string[]): string[] {
  const parts: string[] = [];
  for (const variant of variants) {
    const words = variant.split(RE_NAME_WORDS);
    if (words.length < 2) {
      continue;
    }
    for (const word of words) {
      const clean = word.replace(RE_TRAILING_DOT, "");
      if (clean.length >= MIN_PART && !PARTICLES.has(clean.toLowerCase())) {
        parts.push(clean);
      }
    }
  }
  return parts;
}

function addExplicit(
  entities: readonly Entity[],
  matchCase: boolean
): Map<string, VariantInfo> {
  const lookup = new Map<string, VariantInfo>();
  entities.forEach((entity, index) => {
    for (const variant of entity.variants) {
      const key = norm(variant, matchCase);
      const existing = lookup.get(key);
      if (!existing || (existing.capsOnly && !entity.implicit)) {
        lookup.set(key, { entity: index, capsOnly: Boolean(entity.implicit) });
      }
    }
  });
  return lookup;
}

function addParts(
  entities: readonly Entity[],
  lookup: Map<string, VariantInfo>,
  matchCase: boolean
): string[] {
  const owners = new Map<string, Set<number>>();
  entities.forEach((entity, index) => {
    if (entity.category !== "person") {
      return;
    }
    for (const part of nameParts(entity.variants)) {
      const key = norm(part, matchCase);
      if (!lookup.has(key)) {
        owners.set(key, (owners.get(key) ?? new Set()).add(index));
      }
    }
  });
  const ambiguous: string[] = [];
  for (const [key, set] of owners) {
    const [only] = [...set];
    if (set.size === 1 && only !== undefined) {
      lookup.set(key, { entity: only, capsOnly: true });
    } else {
      ambiguous.push(key);
    }
  }
  return ambiguous;
}

function buildMatcher(
  entities: readonly Entity[],
  options: AnonymizeOptions
): Matcher {
  const lookup = addExplicit(entities, options.matchCase);
  const ambiguous = options.nameParts
    ? addParts(entities, lookup, options.matchCase)
    : [];
  const keys = [...lookup.keys()].sort((a, b) => b.length - a.length);
  if (keys.length === 0) {
    return { regex: null, lookup, ambiguous };
  }
  const body = keys
    .map((key) => key.replace(RE_ESCAPE, "\\$&").replace(RE_SPACES, "\\s+"))
    .join("|");
  const regex = new RegExp(
    `(?<![\\p{L}\\p{N}_])(?:${body})(?![\\p{L}\\p{N}_])`,
    options.matchCase ? "gu" : "giu"
  );
  return { regex, lookup, ambiguous };
}

function seedEntities(seeds: readonly KeyRow[]): Entity[] {
  const grouped = new Map<string, Entity>();
  for (const row of seeds) {
    if (!(LIST_CATEGORIES as readonly string[]).includes(row.category)) {
      continue;
    }
    const id = `${row.category}|${row.pseudonym}`;
    const entity = grouped.get(id) ?? {
      category: row.category as ListCategory,
      variants: [],
      replacement: row.pseudonym,
    };
    entity.variants.push(row.original);
    grouped.set(id, entity);
  }
  return [...grouped.values()];
}

function withHintedNames(
  text: string,
  entities: Entity[],
  options: AnonymizeOptions
): Matcher {
  let matcher = buildMatcher(entities, options);
  const names = hintedNames(text, options).sort(
    (a, b) => b.split(RE_NAME_WORDS).length - a.split(RE_NAME_WORDS).length
  );
  let added = false;
  for (const name of names) {
    if (matcher.lookup.has(norm(name, options.matchCase))) {
      continue;
    }
    entities.push({ category: "person", variants: [name], implicit: true });
    matcher = buildMatcher(entities, options);
    added = true;
  }
  return added ? matcher : buildMatcher(entities, options);
}

function entityMatches(text: string, matcher: Matcher, matchCase: boolean) {
  const found: RawMatch[] = [];
  if (!matcher.regex) {
    return found;
  }
  for (const match of text.matchAll(matcher.regex)) {
    const info = matcher.lookup.get(norm(match[0], matchCase));
    if (match.index === undefined || !info) {
      continue;
    }
    if (info.capsOnly && !RE_UPPER_START.test(match[0])) {
      continue;
    }
    found.push({
      start: match.index,
      end: match.index + match[0].length,
      category: "person",
      identity: `e${info.entity}`,
      priority: 0,
    });
  }
  return found;
}

function patternMatches(text: string, options: AnonymizeOptions): RawMatch[] {
  return detectPatterns(text, options.detect).map(
    (hit: PatternHit): RawMatch => ({
      start: hit.start,
      end: hit.end,
      category: hit.category,
      identity: `${hit.category}|${hit.identity}`,
      priority: 1,
    })
  );
}

/** Longest span first at any start; nothing may overlap an earlier choice. */
function resolveOverlaps(raw: readonly RawMatch[]): RawMatch[] {
  const sorted = [...raw].sort(
    (a, b) =>
      a.start - b.start ||
      b.end - b.start - (a.end - a.start) ||
      a.priority - b.priority
  );
  const kept: RawMatch[] = [];
  let cursor = 0;
  for (const match of sorted) {
    if (match.start >= cursor) {
      kept.push(match);
      cursor = match.end;
    }
  }
  return kept;
}

function categoryTag(category: AnonCategory, options: AnonymizeOptions) {
  const word = options.labels[category].toUpperCase().replace(RE_SPACES, "_");
  return `[${word}]`;
}

interface State {
  options: AnonymizeOptions;
  counters: Map<AnonCategory, number>;
  used: Set<string>;
  assigned: Map<string, string>;
}

function numbered(category: AnonCategory, state: State): string {
  const { options } = state;
  if (options.style === "category") {
    return categoryTag(category, options);
  }
  let n = state.counters.get(category) ?? 0;
  let candidate: string;
  do {
    n += 1;
    candidate =
      options.style === "tags"
        ? `[${options.labels[category].toUpperCase().replace(RE_SPACES, "_")}_${n}]`
        : `${options.labels[category]} ${n}`;
  } while (state.used.has(candidate));
  state.counters.set(category, n);
  state.used.add(candidate);
  return candidate;
}

function keyId(
  category: AnonCategory,
  original: string,
  options: AnonymizeOptions
): string {
  if ((LIST_CATEGORIES as readonly string[]).includes(category)) {
    return `${category}|${norm(original, options.matchCase)}`;
  }
  return `${category}|${norm(original, false)}`;
}

export function emptyCounts(): Record<AnonCategory, number> {
  return {
    person: 0,
    place: 0,
    organisation: 0,
    other: 0,
    email: 0,
    phone: 0,
    url: 0,
    handle: 0,
    ip: 0,
    postcode: 0,
    id: 0,
    date: 0,
  };
}

/** A year-only date is many-to-one, so it has nothing to put in a key. */
function isReversible(
  category: AnonCategory,
  original: string,
  options: AnonymizeOptions
) {
  if (options.style === "category") {
    return false;
  }
  return !(
    category === "date" &&
    options.dateMode === "year" &&
    yearOfDate(original)
  );
}

interface Run {
  entities: Entity[];
  state: State;
  keyMap: Map<string, KeyRow>;
}

function startRun(
  entities: Entity[],
  seeds: readonly KeyRow[],
  options: AnonymizeOptions
): Run {
  const state: State = {
    options,
    counters: new Map(),
    used: new Set(),
    assigned: new Map(),
  };
  for (const entity of entities) {
    if (entity.replacement) {
      state.used.add(entity.replacement);
    }
  }
  const keyMap = new Map<string, KeyRow>();
  for (const seed of seeds) {
    state.used.add(seed.pseudonym);
    if (!(LIST_CATEGORIES as readonly string[]).includes(seed.category)) {
      state.assigned.set(
        keyId(seed.category, seed.original, options),
        seed.pseudonym
      );
    }
    keyMap.set(keyId(seed.category, seed.original, options), seed);
  }
  return { entities, state, keyMap };
}

function pseudonymFor(
  match: RawMatch,
  category: AnonCategory,
  original: string,
  entity: Entity | undefined,
  run: Run
): string {
  const { state } = run;
  const { options } = state;
  if (category === "date" && options.dateMode === "year") {
    const year = yearOfDate(original);
    if (year) {
      return year;
    }
  }
  const known = state.assigned.get(match.identity);
  if (known) {
    return known;
  }
  const seeded = state.assigned.get(keyId(category, original, options));
  const value = entity?.replacement ?? seeded ?? numbered(category, state);
  state.assigned.set(match.identity, value);
  return value;
}

function recordKey(replacement: Replacement, run: Run): void {
  const { options } = run.state;
  if (!isReversible(replacement.category, replacement.original, options)) {
    return;
  }
  const id = keyId(replacement.category, replacement.original, options);
  const row = run.keyMap.get(id);
  if (row) {
    row.count += 1;
    return;
  }
  run.keyMap.set(id, {
    category: replacement.category,
    original: replacement.original,
    pseudonym: replacement.replacement,
    count: 1,
  });
}

function applyMatches(
  text: string,
  kept: readonly RawMatch[],
  run: Run
): { output: string; replacements: Replacement[] } {
  const replacements: Replacement[] = [];
  let output = "";
  let cursor = 0;
  for (const match of kept) {
    const original = text.slice(match.start, match.end);
    const entity = match.identity.startsWith("e")
      ? run.entities[Number(match.identity.slice(1))]
      : undefined;
    const category = entity ? entity.category : match.category;
    const replacement: Replacement = {
      start: match.start,
      end: match.end,
      category,
      original,
      replacement: pseudonymFor(match, category, original, entity, run),
    };
    output += text.slice(cursor, match.start) + replacement.replacement;
    cursor = match.end;
    replacements.push(replacement);
    recordKey(replacement, run);
  }
  return { output: output + text.slice(cursor), replacements };
}

/** Replace the identifiers in `text`. Everything happens in this call; nothing is stored or sent. */
export function anonymize(
  text: string,
  options: AnonymizeOptions
): AnonymizeResult {
  const seeds = parseKey(options.existingKey);
  const entities: Entity[] = [
    ...seedEntities(seeds),
    ...LIST_CATEGORIES.flatMap((category) =>
      parseTermList(options.lists[category], category)
    ),
  ];
  const matcher = withHintedNames(text, entities, options);
  const kept = resolveOverlaps([
    ...entityMatches(text, matcher, options.matchCase),
    ...patternMatches(text, options),
  ]);
  const run = startRun(entities, seeds, options);
  const { output, replacements } = applyMatches(text, kept, run);
  const counts = emptyCounts();
  for (const replacement of replacements) {
    counts[replacement.category] += 1;
  }
  return {
    output,
    replacements,
    key: [...run.keyMap.values()],
    counts,
    ambiguous: matcher.ambiguous,
    candidates: suggestCandidates(text, replacements),
  };
}
